/**
 * Slim band stacked above the WorkbenchPromptCard body (Claude-style notice row).
 *
 * @param {{
 *   message: import('react').ReactNode,
 *   primaryLabel?: string,
 *   onPrimary?: () => void,
 *   onDismiss?: () => void,
 *   progressPct?: number|null,   // 0..100 → thin L→R bar stuck to the top border
 * }} props
 */
import React from 'react';
import { X } from 'lucide-react';

export default function WorkbenchPromptAlertBand({
  message, primaryLabel, onPrimary, onDismiss, primaryTone = 'primary', progressPct = null,
}) {
  const showPrimary = Boolean(primaryLabel && onPrimary);
  const showDismiss = Boolean(onDismiss);
  const primaryClass = primaryTone === 'danger'
    ? 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-500'
    : 'bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-500';
  // Clamp to a sane 0..100; render the ring only for a real numeric value.
  const pct = typeof progressPct === 'number' && Number.isFinite(progressPct)
    ? Math.max(0, Math.min(100, progressPct))
    : null;

  return (
    <div className="flex w-full items-center justify-between gap-1.5 font-normal text-[0.65rem] leading-snug text-gray-600 sm:text-xs">
      {pct != null ? (
        <div
          className="relative shrink-0"
          style={{ width: 36, height: 36 }}
          role="img"
          aria-label={`${pct}% complete`}
          title={`${pct}% complete`}
        >
          {/* SVG ring — same construction as the plan-points indicator, blue.
              circumference = 2πr (r=16.5); dashoffset leaves (100-pct)% unfilled. */}
          <svg width="36" height="36" className="-rotate-90">
            <circle cx="18" cy="18" r="16.5" fill="none" strokeWidth="3" className="stroke-slate-200" />
            <circle
              cx="18" cy="18" r="16.5" fill="none" strokeWidth="3" strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 16.5}
              strokeDashoffset={(2 * Math.PI * 16.5) * (1 - pct / 100)}
              className="stroke-blue-500"
            />
          </svg>
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="text-[10px] font-semibold tabular-nums leading-none text-slate-700">{pct}%</span>
          </div>
        </div>
      ) : null}
      <span className="min-w-0 flex-1 text-sm font-normal text-gray-600 sm:text-sm">{message}</span>
      {(showPrimary || showDismiss) && (
        <span className="flex shrink-0 items-center gap-1.5">
          {showPrimary ? (
            <button
              type="button"
              onClick={() => onPrimary?.()}
              className={`inline-flex h-8 min-w-[4rem] shrink-0 items-center justify-center whitespace-nowrap rounded-md px-3 text-xs font-semibold text-white shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${primaryClass}`}
            >
              {primaryLabel}
            </button>
          ) : null}
          {showDismiss ? (
            <button
              type="button"
              onClick={() => onDismiss?.()}
              className="ml-2 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-transparent bg-transparent text-gray-500 transition-colors hover:bg-black/5 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              aria-label="Dismiss notice"
            >
              <X className="h-3 w-3" aria-hidden strokeWidth={2.5} />
            </button>
          ) : null}
        </span>
      )}
    </div>
  );
}
